import React, { useState, useEffect } from 'react';

import { useParams, useHistory } from 'react-router-dom';

import { parseISO, addHours } from 'date-fns';

import { format } from 'date-fns-tz';

import { Document, Page, pdfjs } from 'react-pdf';

import { Exam } from '~/modules/pharmacist/pages';
import PrescriptionPage from '~/modules/patient/pages/prescription';
import {
  Container,
  HeaderContainer,
  Header,
  InfosHeader,
  TextHeader,
  BoxData,
  SpanData,
  BackgroundHeader,
  CardsContainer,
  FadeInContainer,
  PdfContainer,
  NotFoundReaderPdfButton,
} from './styles';

import { Modal, CodeValidator } from '~/shared/components';
import PrescriptionItem from './components/prescriptionItem';
import LoaderComponent from '~/shared/components/loader';
import api from '~/services/api';
import { LogoImage } from '~/shared/components/logoImage';
import validateHashOrigin from '~/utils';

const DOCUMENT_TYPES = [
  {
    key: 'medication_prescription',
    title: 'Receita Digital',
    icon: 'prescription',
  },
  { key: 'sick_note', title: 'Atestado Médico', icon: 'sick_note' },
  { key: 'medical_exam', title: 'Solicitação de Exames', icon: 'exam' },
  { key: 'medical_report', title: 'Relatório Médico', icon: 'report' },
  {
    key: 'custom_document',
    title: 'Outros Documentos',
    icon: 'other_documents',
  },
];

function buildPrescriptionItems(data) {
  let counter = 0;
  const items = DOCUMENT_TYPES.map((type, index) => {
    const docData = data[type.key];
    const contain = docData?.contain;
    return {
      localId: index + 1,
      id: contain ? docData.classification_id : null,
      title: type.title,
      show: !!contain,
      icon: type.icon,
    };
  });

  counter = items.filter((item) => item.show).length;
  return { items, counter };
}

function formatEncounterDate(datetime) {
  const addedDate = addHours(parseISO(datetime), 3);
  return format(addedDate, "dd/MM/yyyy 'às' HH:mm", {
    timeZone: 'America/Sao_Paulo',
  });
}

function isDetailView(selectedItem) {
  return selectedItem?.localId === 1 || selectedItem?.localId === 3;
}

function PatientHome() {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  const [numPages, setNumPages] = useState(null);
  const [prescriptionItems, setPrescriptionItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalIsOpen, setModalAsOpen] = useState(false);
  const [encounterDate, setEncounterDate] = useState(null);
  const [pdfOpen, setpdfOpen] = useState(false);
  const [pdfModalTitle, setPdfModalTitle] = useState('');
  const [docCount, setDocCount] = useState(0);
  const [pdfItems, setpdfItems] = useState('');
  const [pdfItemShowing, setpdfItemShowing] = useState('');
  const [validationKey, setValidationKey] = useState(null);
  const params = useParams();
  const history = useHistory();

  const onDocumentLoadSuccess = ({ numPages: loadedPages }) => {
    setNumPages(loadedPages);
  };

  useEffect(() => {
    setLoading(true);
    api
      .get(`/document/${validateHashOrigin(params.id)}`)
      .then(({ data }) => {
        setValidationKey(data.validation_key);
        setEncounterDate(formatEncounterDate(data.datetime));

        const { items, counter } = buildPrescriptionItems(data);
        setPrescriptionItems(items);
        setDocCount(counter);
      })
      .catch(() => {
        history.push('/404');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedItem]);

  const postDataAndOpenModal = async (value) => {
    const hashId = validateHashOrigin(params.id);
    const { data } = await api.post(
      `/document/doctor/${hashId}/${selectedItem.id}`,
      { validation_key: value }
    );

    const { data: responseData } = await api.get(
      `/document/file/${hashId}/${selectedItem.id}`,
      {
        validation_key: value,
        headers: { Authorization: `Bearer ${data.accessToken}` },
      }
    );

    setpdfItemShowing(responseData[0].url);
    setpdfItems(responseData);
    setpdfOpen(true);
    setModalAsOpen(false);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    if ([2, 4, 5].includes(item.localId)) {
      setModalAsOpen(true);
      setPdfModalTitle(item.title);
    }
  };

  const renderSelectedItem = () => {
    if (selectedItem?.localId === 1) {
      return (
        <PrescriptionPage
          title={selectedItem.title}
          id={selectedItem.id}
          token={params.id}
          validationKey={validationKey}
          goBack={() => setSelectedItem(null)}
        />
      );
    }
    if (selectedItem?.localId === 3) {
      return (
        <Exam
          title={selectedItem.title}
          id={selectedItem.id}
          token={params.id}
          validationKey={validationKey}
          selectedItem={() => setSelectedItem(null)}
        />
      );
    }
    return <></>;
  };

  if (isDetailView(selectedItem)) {
    return <Container>{renderSelectedItem()}</Container>;
  }

  return (
    <FadeInContainer>
      {loading && <LoaderComponent />}
      <HeaderContainer>
        <BackgroundHeader BackgroundHeight="288px" />
        <Header>
          <LogoImage />
        </Header>
        <InfosHeader>
          <TextHeader>
            {docCount > 1
              ? 'Consulte os documentos gerados no seu atendimento'
              : 'Consulte o documento gerado no seu atendimento'}
          </TextHeader>
          <BoxData>
            <SpanData>DATA:</SpanData> {encounterDate}
          </BoxData>
        </InfosHeader>
      </HeaderContainer>
      <CardsContainer>
        {prescriptionItems
          .filter((item) => item.show)
          .map((item) => (
            <PrescriptionItem
              title={item.title}
              key={`${item.title}-${item.localId}`}
              icon={item.icon}
              onClick={() => handleItemClick(item)}
            />
          ))}
      </CardsContainer>
      {modalIsOpen && (
        <Modal
          widthContainer="350px"
          heightContainer="330px"
          isOpen={modalIsOpen}
          onCloseRequest={(event) => {
            event.preventDefault();
            setModalAsOpen(!modalIsOpen);
          }}
        >
          <CodeValidator
            width="165px"
            title="Desbloquear"
            fontSize="16px"
            height="50px"
            validationKey={validationKey}
            postData={(value) => postDataAndOpenModal(value)}
          />
        </Modal>
      )}
      {pdfOpen && (
        <Modal
          isPdfShowing="true"
          widthContainer="100%"
          headerSize="100vw"
          heightContainer="calc(100vh + 240px)"
          isOpen={pdfOpen}
          title={pdfModalTitle}
          onCloseRequest={(event) => {
            event.preventDefault();
            setpdfOpen(!pdfOpen);
          }}
          itens={pdfItems}
          onClickItens={(item) => {
            setpdfItemShowing(item.url);
          }}
        >
          <PdfContainer>
            <Document
              file={pdfItemShowing}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<LoaderComponent />}
              height="auto"
              width="100vw"
              renderMode="canvas"
            >
              {Array.from(new Array(numPages), (el, index) => (
                <Page key={`page_${index + 1}`} pageNumber={index + 1} />
              ))}
            </Document>

            <NotFoundReaderPdfButton>
              <a href={pdfItemShowing} target="_blank" rel="noreferrer">
                Fazer o download
              </a>
            </NotFoundReaderPdfButton>
          </PdfContainer>
        </Modal>
      )}
    </FadeInContainer>
  );
}

export default PatientHome;
